import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';

/**
 * Dynasty League Formula
 * Multi-year value with heavy age curves and rookie upside
 * Based on long-term asset value rather than single-season production
 */

export interface DynastyFormulaWeights {
  ageCurve: number;           // Most important - age trajectory
  rookieUpside: number;       // Rookie/young player potential
  careerLongevity: number;    // Position-specific career length
  previousPPG: number;        // Proven production
  projectedPoints: number;    // Current year value
  breakoutPotential: number;  // Upside for young players
  positionLongevity: number;  // How long positions stay relevant
  injuryHistory: number;      // Long-term health concerns
  teamSituation: number;      // Stable situation value
  tradeValue: number;         // Dynasty trade market value
}

export const DYNASTY_WEIGHTS: DynastyFormulaWeights = {
  ageCurve: 0.30,            // Highest weight - age is king
  rookieUpside: 0.20,        // Rookie potential crucial
  careerLongevity: 0.15,     // Long-term value
  previousPPG: 0.12,         // Some weight on production
  projectedPoints: 0.08,     // Less weight on current year
  breakoutPotential: 0.06,   // Young player upside
  positionLongevity: 0.04,   // Career length by position
  injuryHistory: 0.02,       // Health concerns
  teamSituation: 0.02,       // Team stability
  tradeValue: 0.01          // Market value
};

// Dynasty age curves by position (peak and decline rates)
const DYNASTY_AGE_CURVES = {
  QB: { peak: 27, decline: 35, shelfLife: 8 },
  RB: { peak: 24, decline: 28, shelfLife: 4 },
  WR: { peak: 26, decline: 31, shelfLife: 5 },
  TE: { peak: 27, decline: 32, shelfLife: 5 },
  K: { peak: 28, decline: 38, shelfLife: 10 },
  DEF: { peak: 0, decline: 0, shelfLife: 2 }  // Team defenses change
};

// Rookie/young player upside multipliers - REDUCED QB dominance
const ROOKIE_UPSIDE_MULTIPLIERS = {
  QB: { rookie: 1.8, year2: 1.5, year3: 1.3 },  // REDUCED from 2.5/2.0/1.5
  RB: { rookie: 1.8, year2: 1.4, year3: 1.2 },
  WR: { rookie: 2.2, year2: 1.8, year3: 1.4 },  // WRs prioritized in dynasty
  TE: { rookie: 1.6, year2: 1.3, year3: 1.1 },
  K: { rookie: 1.0, year2: 1.0, year3: 1.0 },
  DEF: { rookie: 1.0, year2: 1.0, year3: 1.0 }
};

export function calculateDynastyValue(
  player: Player,
  leagueConfig: LeagueConfig
): number {
  const weights = DYNASTY_WEIGHTS;
  
  // Core dynasty factors
  const ageCurve = calculateDynastyAgeCurve(player);
  const rookieUpside = calculateRookieUpside(player);
  const careerLongevity = calculateCareerLongevity(player);
  const previousPPG = (player.lastYearPoints || 0) / 17;
  const projectedPoints = player.projection || player.projectedPoints || 0;
  const breakoutPotential = calculateBreakoutPotential(player);
  const positionLongevity = calculatePositionLongevity(player);
  const injuryHistory = calculateInjuryHistory(player);
  const teamSituation = calculateTeamSituation(player);
  const tradeValue = calculateTradeValue(player);
  
  // Calculate weighted score
  const dynastyValue = 
    (weights.ageCurve * ageCurve) +
    (weights.rookieUpside * rookieUpside) +
    (weights.careerLongevity * careerLongevity) +
    (weights.previousPPG * previousPPG) +
    (weights.projectedPoints * projectedPoints) +
    (weights.breakoutPotential * breakoutPotential) +
    (weights.positionLongevity * positionLongevity) +
    (weights.injuryHistory * injuryHistory) +
    (weights.teamSituation * teamSituation) +
    (weights.tradeValue * tradeValue);
  
  // Apply multi-year NPV calculation
  const npvMultiplier = calculateNPV(player);
  
  // Apply QB saturation penalty for Dynasty (target: 5 QBs in top 24)
  const qbSaturationPenalty = calculateDynastyQBSaturation(player);
  
  return dynastyValue * npvMultiplier * qbSaturationPenalty;
}

function calculateDynastyAgeCurve(player: Player): number {
  const age = player.age || 26;
  const position = player.position;
  const curve = DYNASTY_AGE_CURVES[position as keyof typeof DYNASTY_AGE_CURVES];
  
  if (!curve) return 0.5;
  
  // Before peak - increasing value
  if (age < curve.peak) {
    const yearsFromPeak = curve.peak - age;
    return Math.min(1.5, 1.0 + (yearsFromPeak * 0.1));
  }
  
  // At peak
  if (age === curve.peak) return 1.5;
  
  // After peak - declining value
  const yearsAfterPeak = age - curve.peak;
  const declineRate = position === 'RB' ? 0.2 : 0.1; // RBs decline faster
  
  return Math.max(0.2, 1.5 - (yearsAfterPeak * declineRate));
}

function calculateRookieUpside(player: Player): number {
  const age = player.age || 26;
  const position = player.position;
  const multipliers = ROOKIE_UPSIDE_MULTIPLIERS[position as keyof typeof ROOKIE_UPSIDE_MULTIPLIERS];
  
  if (!multipliers) return 1.0;
  
  if (age <= 22) return multipliers.rookie;     // Rookie year
  if (age <= 23) return multipliers.year2;     // Year 2
  if (age <= 24) return multipliers.year3;     // Year 3
  
  return 1.0; // No rookie upside
}

function calculateCareerLongevity(player: Player): number {
  const age = player.age || 26;
  const position = player.position;
  const curve = DYNASTY_AGE_CURVES[position as keyof typeof DYNASTY_AGE_CURVES];
  
  if (!curve) return 0.5;
  
  // Years remaining in prime/productive career
  const yearsRemaining = Math.max(0, curve.decline - age);
  const totalShelfLife = curve.shelfLife;
  
  return Math.min(1.0, yearsRemaining / totalShelfLife);
}

function calculateBreakoutPotential(player: Player): number {
  const age = player.age || 26;
  const position = player.position;
  
  // Young players with low current production have breakout potential
  if (age > 25) return 0.5; // Limited upside after 25
  
  const currentProduction = (player.lastYearPoints || 0) / 17;
  
  // High upside if young with low current production
  if (age <= 23 && currentProduction < 10) {
    if (position === 'WR') return 1.5; // WRs break out often
    if (position === 'RB') return 1.3; // RBs can break out
    if (position === 'TE') return 1.2; // TEs break out later
    if (position === 'QB') return 1.4; // QBs can break out big
  }
  
  return 1.0;
}

function calculatePositionLongevity(player: Player): number {
  const position = player.position;
  
  // Positions by typical dynasty career length - REDUCED QB premium
  const longevity = {
    QB: 1.0,  // REDUCED - QBs last longest but not overpowered
    WR: 1.2,  // WRs have long careers - prioritize these
    TE: 1.1,  // TEs last fairly long
    RB: 0.7,  // RBs have shortest careers but still need some
    K: 0.4,   // Rarely kept in dynasty
    DEF: 0.2  // Almost never kept
  };
  
  return longevity[position as keyof typeof longevity] || 0.5;
}

function calculateInjuryHistory(player: Player): number {
  // Mock - would use real injury data
  const age = player.age || 26;
  const position = player.position;
  
  // Injury risk by position and age
  let injuryMultiplier = 1.0;
  
  if (position === 'RB') injuryMultiplier *= 0.8; // RBs get hurt more
  if (age > 28) injuryMultiplier *= 0.9;          // Older players more fragile
  
  return injuryMultiplier;
}

function calculateTeamSituation(player: Player): number {
  // Mock - would analyze coaching stability, team building direction
  const stableTeams = ['KC', 'SF', 'BUF', 'DAL']; // Teams with stable situations
  
  if (stableTeams.includes(player.team)) return 1.1;
  return 1.0;
}

function calculateTradeValue(player: Player): number {
  // Mock - would use real dynasty trade values (KTC, etc.)
  const age = player.age || 26;
  
  if (age <= 24) return 1.2; // Young players have high trade value
  if (age <= 27) return 1.0; // Prime players
  return 0.8;               // Older players harder to trade
}

function calculateNPV(player: Player): number {
  // Net Present Value over multiple years
  const age = player.age || 26;
  const position = player.position;
  const curve = DYNASTY_AGE_CURVES[position as keyof typeof DYNASTY_AGE_CURVES];
  
  if (!curve) return 1.0;
  
  let npv = 0;
  const discountRate = 0.15; // 15% discount per year
  
  // Project value over next 5 years
  for (let year = 0; year < 5; year++) {
    const futureAge = age + year;
    let yearValue = 1.0;
    
    // Apply age curve to future years
    if (futureAge <= curve.peak) {
      yearValue = 1.0 + ((curve.peak - futureAge) * 0.05);
    } else {
      const yearsAfterPeak = futureAge - curve.peak;
      const declineRate = position === 'RB' ? 0.25 : 0.15;
      yearValue = Math.max(0.1, 1.0 - (yearsAfterPeak * declineRate));
    }
    
    // Discount future value
    const discountedValue = yearValue / Math.pow(1 + discountRate, year);
    npv += discountedValue;
  }
  
  return npv / 5; // Average across 5 years
}

export function getDynastyPositionPriority(): string[] {
  // Youth and longevity prioritized
  return ['WR', 'QB', 'WR', 'RB', 'WR', 'TE', 'WR', 'QB', 'RB', 'WR'];
}

export function getDynastyDraftStrategy(): string[] {
  return [
    'Prioritize youth over current production - age is king',
    'WRs have longest careers - safe dynasty investments',
    'Young QBs are league cornerstones - draft early if elite',
    'RBs have shortest careers - avoid aging backs',
    'Elite young TEs (LaPorta, Kincaid) have long-term value', 
    'Rookies and 2nd-year players have highest upside',
    'Trade aging veterans for young assets + picks',
    'Focus on 3-5 year outlook, not just current season'
  ];
}

export function getDynastyAgeTargets() {
  return {
    QB: { target: 25, max: 30 },
    RB: { target: 23, max: 26 },
    WR: { target: 24, max: 28 },
    TE: { target: 24, max: 29 },
    K: { target: 0, max: 0 },   // Rarely drafted
    DEF: { target: 0, max: 0 }  // Almost never kept
  };
}

function calculateDynastyQBSaturation(player: Player): number {
  if (player.position !== 'QB') return 1.0;
  
  // Dynasty should have moderate QB presence (target: 5 QBs in top 24)
  // Elite young QBs valuable, but not overwhelming
  const qbRank = getQBTier(player);
  
  if (qbRank <= 3) return 1.0;      // Elite QBs - no penalty
  if (qbRank <= 6) return 0.8;      // Good QBs - 20% penalty  
  if (qbRank <= 9) return 0.5;      // Mid QBs - 50% penalty
  if (qbRank <= 12) return 0.3;     // Lower QBs - 70% penalty
  return 0.1;                       // QB13+ - 90% penalty
}

function getQBTier(player: Player): number {
  // Mock QB ranking - would use real consensus rankings  
  const tierOneQBs = ['Josh Allen', 'Lamar Jackson', 'Jalen Hurts'];
  const tierTwoQBs = ['Joe Burrow', 'Jayden Daniels', 'Dak Prescott', 'Tua Tagovailoa', 'Brock Purdy', 'CJ Stroud'];
  const tierThreeQBs = ['Kyler Murray', 'Aaron Rodgers', 'Trevor Lawrence', 'Jordan Love', 'Caleb Williams', 'Anthony Richardson'];
  
  if (tierOneQBs.some(name => player.name.includes(name.split(' ')[1]))) return 1;
  if (tierTwoQBs.some(name => player.name.includes(name.split(' ')[1]))) return 4;  
  if (tierThreeQBs.some(name => player.name.includes(name.split(' ')[1]))) return 8;
  
  return 15; // Default lower tier
}